import { __rest } from 'tslib';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useMemo, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { isArray, isObject, isEmpty } from 'lodash';
import StyledTableHeaderCell from './StyledTableHeaderCell.js';
import { DragDummyPreview, Container, DropHandler, ResizeHandler, CellDivider } from './styled.js';
import DragHandler from './DragHandler.js';
import Header from './Header.js';

const TableHeaderCell = React__default.memo((_a) => {
    var _b;
    var { children, column, onColumnDrop, disabled, enableColumnDragging, disableRowFiltering, disableSortBy, disableResizing, rtl, headerCellPadding, columnOrder } = _a, rest = __rest(_a, ["children", "column", "onColumnDrop", "disabled", "enableColumnDragging", "disableRowFiltering", "disableSortBy", "disableResizing", "rtl", "headerCellPadding", "columnOrder"]);
    const columnId = column.id;
    const [dragDirection, setDragDirection] = useState();
    const previewRef = useRef(null);
    const filterEnabled = useMemo(() => {
        return !disableRowFiltering && column.canFilter && column.Filter !== undefined;
    }, [column.Filter, column.canFilter, disableRowFiltering]);
    const sortEnabled = useMemo(() => {
        return !disableSortBy && column.canSort;
    }, [column.canSort, disableSortBy]);
    const dragEnabled = useMemo(() => {
        return enableColumnDragging && !column.disableDrag;
    }, [column, enableColumnDragging]);
    const resizeEnabled = useMemo(() => {
        return (column === null || column === void 0 ? void 0 : column.canResize) && !!(column === null || column === void 0 ? void 0 : column.getResizerProps) && !disableResizing;
    }, [column === null || column === void 0 ? void 0 : column.canResize, column === null || column === void 0 ? void 0 : column.getResizerProps, disableResizing]);
    const updateDragDirection = useCallback((itemId) => {
        const columnIndex = columnOrder.findIndex(id => columnId === id);
        const itemIndex = columnOrder.findIndex(id => itemId === id);
        if (columnIndex === itemIndex) {
            setDragDirection(undefined);
            return;
        }
        setDragDirection(itemIndex > columnIndex ? 'before' : 'after');
    }, [columnOrder, columnId]);
    const [{ canDrag, isDragging }, drag, preview] = useDrag(() => ({
        type: 'Col',
        item: { columnId },
        canDrag: dragEnabled || false,
        collect: monitor => ({
            canDrag: dragEnabled || false,
            isDragging: monitor.isDragging(),
        }),
    }), [dragEnabled]);
    preview(previewRef);
    const [{ canDrop, isOver }, drop] = useDrop(() => ({
        accept: 'Col',
        item: { columnId },
        collect: monitor => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
        hover(item) {
            updateDragDirection(item.columnId);
        },
        drop(item) {
            if (columnId === item.columnId) {
                setDragDirection(undefined);
                return;
            }
            onColumnDrop(item.columnId, columnId, dragDirection);
        },
    }), [updateDragDirection, dragDirection]);
    const isDragActive = isOver && canDrop;
    const isCheckboxColumn = column.id === 'selection';
    const isFiltered = useMemo(() => {
        var _a;
        if (!filterEnabled) {
            return false;
        }
        const filterValue = (_a = column === null || column === void 0 ? void 0 : column.filterValue) === null || _a === void 0 ? void 0 : _a.value;
        if (isArray(filterValue)) {
            return filterValue.length > 0;
        }
        if (isObject(filterValue)) {
            return !isEmpty(filterValue);
        }
        return filterValue !== undefined && filterValue !== null && filterValue !== '';
    }, [(_b = column === null || column === void 0 ? void 0 : column.filterValue) === null || _b === void 0 ? void 0 : _b.value, filterEnabled]);
    const shouldShowResize = useMemo(() => !isDragActive && !disabled && !isCheckboxColumn && resizeEnabled, [isDragActive, disabled, isCheckboxColumn, resizeEnabled]);
    return (jsxs(Fragment, { children: [isDragging && jsx(DragDummyPreview, { ref: previewRef }), jsxs(StyledTableHeaderCell, Object.assign({ rtl: rtl, component: "div", isDragging: isDragging }, rest, column.getHeaderProps(), { children: [jsxs(Container, Object.assign({ ref: (x) => {
                            drop(x);
                            drag(x);
                        }, rtl: rtl, isSorted: !disabled && (column === null || column === void 0 ? void 0 : column.isSorted), canSort: sortEnabled, isFiltered: isFiltered, disabled: disabled, isCheckboxColumn: isCheckboxColumn }, (column.getSortByToggleProps !== undefined && !disabled ? column.getSortByToggleProps() : {}), { headerCellPadding: headerCellPadding }, { children: [dragEnabled && (jsx(DragHandler, { isDragging: isDragging, visibility: canDrag && !disabled && !isCheckboxColumn ? 'visible' : 'hidden' })), jsx(Header, Object.assign({}, { column, disabled, sortEnabled, isDragging, children, rtl, filterEnabled, isCheckboxColumn }))] })), dragDirection === 'before' && jsx(DropHandler, { "data-testid": "drop-enabled-before", isDragActive: isDragActive }), dragDirection === 'after' && (jsx(DropHandler, { "data-testid": "drop-enabled-after", isDragActive: isDragActive, isAfter: true })), shouldShowResize && (jsx(ResizeHandler, Object.assign({}, column === null || column === void 0 ? void 0 : column.getResizerProps(), { "data-testid": `resize-${column.id}`, isResizing: column.isResizing, rtl: rtl }))), !resizeEnabled && jsx(CellDivider, {})] }))] }));
});
TableHeaderCell.displayName = 'TableHeaderCell';

export { TableHeaderCell as default };
//# sourceMappingURL=TableHeaderCell.js.map
